// Report API — two sources.
// Legacy /report* (analysis_report LLM generator) — kept for the legacy
// Psychologist flow and pre-engine users.
// Engine report (spec §6.5, v2.5) — deterministic GET /engine/report:
// no LLM, no quotes; 429 = cooldown (retry_at), 409 = not engine-enabled.

import { api, API_BASE } from './client'
import { useAuthStore } from '../store/authStore'
import { isNative } from '../lib/platform'
import type { EngineReport } from '../types/api'

interface GenerateParams {
  sessionQuestion?: string
  exclusions?: string
}

// /report/status — throttle + last-report gating (ReportFlowModal).
export interface ReportStatus {
  can_generate?: boolean
  last_report_date?: string | null
  remaining_seconds?: number
}
// /report/generate + /report/job/{id} — async job envelope.
export interface ReportJob {
  status: string
  job_id?: string
  error?: string
}
// /report — the rendered report payload (Report screen).
export interface ReportData {
  report?: Record<string, unknown> | null
}

export const reportApi = {
  status: () => api.get<ReportStatus>('/report/status'),
  get: () => api.get<ReportData>('/report'),
  generate: ({ sessionQuestion = '', exclusions = '' }: GenerateParams = {}) =>
    api.post<ReportJob>('/report/generate', { session_question: sessionQuestion, exclusions }),
  job: (jobId: string) => api.get<ReportJob>(`/report/job/${jobId}`),

  // Engine v2 report (spec §6.5): deterministic, quote-free assembly of the
  // durable sections; cooldown 429 carries retry_at in detail.
  engine: () => api.get<EngineReport>('/engine/report'),

  // Raw fetch for the binary PDF (the JSON client can't return a Blob).
  async pdf(): Promise<Blob> {
    const token = useAuthStore.getState().accessToken
    const res = await fetch(`${API_BASE}/report/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.blob()
  },
}

const PDF_NAME = 'humanteq-report.pdf'

// FileReader gives "data:application/pdf;base64,XXXX"; Filesystem.writeFile
// wants just the payload after the comma.
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '')
    reader.readAsDataURL(blob)
  })
}

// Native save. The web path below (blob: URL + <a download> + click) is a silent
// no-op in the Android WebView: it drops download-attribute navigations to blob:
// URLs unless a DownloadListener is attached, and @capacitor/android attaches
// none — the click just vanishes, with no error to catch. So write the bytes to
// the app's cache dir and hand the resulting file:// URI to the system share
// sheet, where the user picks "Save to Files" or a PDF viewer.
//
// The plugins are imported lazily so they stay out of the web bundle.
async function saveNative(blob: Blob): Promise<void> {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ])
  const data = await blobToBase64(blob)
  // Cache dir, because android/app/src/main/res/xml/file_paths.xml exposes it to
  // the FileProvider the Share plugin resolves file:// URIs through.
  const { uri } = await Filesystem.writeFile({ path: PDF_NAME, data, directory: Directory.Cache })
  try {
    await Share.share({ title: 'Отчёт Humanteq', url: uri, dialogTitle: 'Сохранить отчёт' })
  } catch (err) {
    // Dismissing the share sheet rejects with "Share canceled". That's the user
    // changing their mind, not a failure — the caller would toast an error.
    const message = err instanceof Error ? err.message : String(err)
    if (!/cancel/i.test(message)) throw err
  }
}

export async function downloadReportPdf(): Promise<void> {
  const blob = await reportApi.pdf()
  if (isNative) return saveNative(blob)

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = PDF_NAME
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
